import React, { useEffect } from 'react';
import { Container, Stack, Title, Card, Group, Text, ActionIcon, Alert } from '@mantine/core';
import { IconChevronLeft, IconUser } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChildSummaryFromStudent } from '../../hooks/useParent';
import { useParentChildren } from '../../hooks/parent/useParentChildren';
import { DailyReportsList } from '../../components/parent/reports/DailyReportsList';
import { SmartLoader } from '../../components/common/LoadingStates';

const DailyReports: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();

  // Get children list for child selector
  const {
    data: childrenResponse,
    isLoading: childrenLoading,
    error: childrenError,
  } = useParentChildren();

  const {
    data: child,
    isLoading: childLoading,
    error: childError,
  } = useChildSummaryFromStudent(childId);

  // Extract children array from response
  const children = Array.isArray(childrenResponse)
    ? childrenResponse
    : ((childrenResponse as any)?.items || []);

  // Auto-redirect to first child if no childId provided but children exist
  useEffect(() => {
    if (!childId && children.length > 0 && !childrenLoading) {
      const firstChild = children[0];
      navigate(`/parent/child/${firstChild.id}/reports`, { replace: true });
    }
  }, [childId, children, childrenLoading, navigate]);

  // Show child selector if no childId and children are available
  if (!childId) {
    if (childrenLoading) {
      return (
        <Container size="xl">
          <SmartLoader isLoading={true}>Loading daily reports...</SmartLoader>
        </Container>
      );
    }

    if (childrenError || children.length === 0) {
      return (
        <Container size="xl">
          <Alert color="red" title="No Children Found">
            No children found in your account. Please contact your school administrator.
          </Alert>
        </Container>
      );
    }

    // Show child selector
    return (
      <Container size="xl">
        <Stack gap="md">
          <Title order={2}>Daily Reports</Title>
          <Text c="dimmed">Please select a child to view their daily reports:</Text>

          <Stack gap="sm">
            {children.map((child: any) => (
              <Card key={child.id} withBorder p="md" style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/parent/child/${child.id}/reports`)}>
                <Group gap="md">
                  <IconUser size={24} />
                  <div>
                    <Text fw={500}>{child.first_name || child.display_name || 'Student'}</Text>
                    <Text size="sm" c="dimmed">
                      {child.class_name || 'Class information not available'}
                    </Text>
                  </div>
                </Group>
              </Card>
            ))}
          </Stack>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="xl">
      <Stack gap="lg">
        {/* Header */}
        <SmartLoader
          isLoading={childLoading}
          error={childError as Error | null}
          isEmpty={!child}
        >
          {child && (
            <Card withBorder>
              <Group>
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={() => navigate('/parent/dashboard')}
                >
                  <IconChevronLeft size={20} />
                </ActionIcon>
                <div>
                  <Title order={2}>{child.first_name}'s Daily Reports</Title>
                  <Text size="sm" c="dimmed">
                    Comprehensive daily summaries of activities and progress
                  </Text>
                </div>
              </Group>
            </Card>
          )}
        </SmartLoader>

        {/* Daily Reports List */}
        {child && <DailyReportsList childId={childId} />}
      </Stack>
    </Container>
  );
};

export default DailyReports;
